import { OperationType } from "../types/card-movements.types";

/**
 * Structural shape so both `CardTransaction` and `CardStatementTransaction` satisfy it without the two
 * modules having to know about each other.
 */
export type AmountShape = {
  amount: number;
  currency: string;
  usd_amount: number | null;
  operation_type: OperationType | null;
};

export const formatSignedAmount = (amount: number, currency: string, operationType: OperationType | null) => {
  const sign = operationType === OperationType.CREDIT ? "+" : "−";
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  return `${sign}${formatted}`;
};

export const formatPlainAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
  } catch {
    return `${Math.abs(amount).toFixed(2)} ${currency}`;
  }
};

/**
 * Cards are denominated in USD; the provider settles the ledger in USD even when the consumer transacted in
 * local currency. USD is therefore the headline figure, with the local amount as a muted secondary line only
 * when the two actually differ.
 */
export const resolveUsdAmount = (tx: AmountShape): number | null =>
  tx.usd_amount ?? (tx.currency === "USD" ? tx.amount : null);

export const shouldShowLocalAmount = (tx: AmountShape): boolean =>
  !!tx.currency && tx.currency !== "USD" && tx.amount !== 0;
